import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import PrincipalBar from '../../AppComponents/Commons/PrincipalBar';
import FirstTitle from '../../AppComponents/Commons/FirstTitle';
import ButtonBack from '../../AppComponents/Commons/ButtonBack';

import useGroups from '../../Hooks/General/useGroups';
import GroupService from '../../Services/Licenses/GroupService';
import { parseLicenseCodesCsv } from '../../Utilities/General/Csv';

export default function AssignGroup() {

    const groups = useGroups()

    const [groupId, setGroupId] = useState('')
    const [rawText, setRawText] = useState('')
    const [fileName, setFileName] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [result, setResult] = useState(null)

    const fileInputRef = useRef(null)

    const isFromFile = fileName !== null

    const { hasCodeColumn, codes } = useMemo(() => parseLicenseCodesCsv(rawText), [rawText])

    const showHeaderError = rawText.trim().length > 0 && !hasCodeColumn
    const canSubmit = groupId !== '' && hasCodeColumn && codes.length > 0 && !submitting

    const handleFileChange = e => {
        const file = e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            setRawText(String(reader.result))
            setFileName(file.name)
            setResult(null)
        }
        reader.readAsText(file)
    }

    const handleRemoveFile = () => {
        setFileName(null)
        setRawText('')
        setResult(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleTextChange = e => {
        if (isFromFile) return
        setRawText(e.target.value)
        setResult(null)
    }

    const handleSubmit = () => {
        setSubmitting(true)
        setResult(null)
        GroupService.changeGroupToUsers({ codes, group: Number(groupId) })
            .then(data => {
                setSubmitting(false)
                if (!data) return
                setResult(data)
                if (data.missing_codes.length === 0) {
                    toast.success('Todos los datos se actualizaron correctamente.', {
                        position: 'bottom-left',
                        hideProgressBar: true,
                        className: 'success-toast'
                    })
                } else {
                    toast.warning(`${data.missing_codes.length} código(s) no se encontraron.`, {
                        position: 'bottom-left',
                        hideProgressBar: true,
                        className: 'warning-toast'
                    })
                }
            })
    }

    return (
        <>

            <PrincipalBar />

            <div className="mx-3 mb-5">

                <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-between m-3">
                    <FirstTitle text="Asignar grupo de liberación a usuarios" />
                    <ButtonBack destination="/inicio" text="VOLVER" />
                </div>

                <div className="row">
                    <div className="col-12 col-lg-4 mb-4">
                        <label htmlFor="groupSelect" className="form-label fw-bold">
                            Grupo de liberación
                        </label>
                        <select
                            id="groupSelect"
                            className="form-select"
                            value={groupId}
                            onChange={e => { setGroupId(e.target.value); setResult(null) }}
                        >
                            <option value="" selected disabled>Seleccione un grupo</option>
                            {groups.map(group => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="alert alert-info">
                    <p className="fw-bold mb-2">Formato de listado/CSV válido:</p>
                    <ul className="mb-2">
                        <li>La primera fila debe ser la cabecera (nombre de las columnas) y el resto de las filas son los datos.</li>
                        <li>
                            Es obligatorio que exista una columna llamada exactamente <strong>"Código"</strong> con
                            el código de licencia de cada usuario. El nombre de esta columna es fijo, no se puede modificar.
                        </li>
                        <li>El resto de las columnas son opcionales, pueden incluirse o no.</li>
                        <li>Se aceptan como separador de columnas: coma (,), punto y coma (;) o tabulación (al copiar celdas desde Excel).</li>
                    </ul>
                    <p className="mb-1">Ejemplo válido:</p>
                    <pre className="bg-white border rounded p-2 mb-0">
Código,Nombre{'\n'}ABC123,Juan Pérez{'\n'}XYZ789,María Gómez
                    </pre>
                    <p className="mb-0 mt-2">También es válido tener sólo la columna obligatoria:</p>
                    <pre className="bg-white border rounded p-2 mb-0">
Código{'\n'}ABC123{'\n'}XYZ789
                    </pre>
                </div>

                <div className="mb-4">
                    <label htmlFor="csvInput" className="form-label fw-bold">
                        Listado de usuarios (código de licencia)
                    </label>
                    <div className="d-flex flex-wrap align-items-center mb-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="form-control col-12 col-md-4 me-md-3 mb-2 mb-md-0"
                            onChange={handleFileChange}
                        />
                        {isFromFile &&
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm mb-2 mb-md-0"
                                onClick={handleRemoveFile}
                            >
                                Quitar archivo ({fileName})
                            </button>
                        }
                    </div>
                    <textarea
                        id="csvInput"
                        className="form-control"
                        rows="10"
                        placeholder="Pegue aquí el listado (formato CSV) o adjunte un archivo .csv"
                        value={rawText}
                        readOnly={isFromFile}
                        onChange={handleTextChange}
                    />
                    {showHeaderError &&
                        <small className="text-danger d-block mt-1">
                            La cabecera debe contener una columna llamada exactamente "Código".
                        </small>
                    }
                    {hasCodeColumn &&
                        <small className="text-secondary d-block mt-1">
                            {codes.length} código(s) detectado(s).
                        </small>
                    }
                </div>

                <div className="mb-4">
                    <button
                        type="button"
                        className="btn btn-info text-white py-2 px-4"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {submitting ? 'Asignando...' : 'Asignar grupo de liberación a usuarios'}
                    </button>
                </div>

                {result &&
                    <div className={`alert ${result.missing_codes.length === 0 ? 'alert-success' : 'alert-warning'}`}>
                        {result.missing_codes.length === 0 ?
                            <p className="mb-0">
                                Todos los datos se actualizaron correctamente ({result.updated_codes.length} código(s)).
                            </p>
                            :
                            <>
                                <p className="mb-2">
                                    Se actualizaron {result.updated_codes.length} de {result.updated_codes.length + result.missing_codes.length} código(s).
                                    Los siguientes códigos no se encontraron y no fueron actualizados:
                                </p>
                                <ul className="mb-0">
                                    {result.missing_codes.map(code => (
                                        <li key={code}>{code}</li>
                                    ))}
                                </ul>
                            </>
                        }
                    </div>
                }

            </div>

        </>
    )
}
